// frontend/src/components/Requisitions/edit/RequisitionEditWizard.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import apiClient from "../../../api/apiClient";
import LoadingSpinner from "../../UI/LoadingSpinner";

import {
  LIST_ROUTE,
  SHOW_REGISTER_PRODUCT_BUTTON,
  CATALOG_META,
  STEP2_SPECS,
} from "./constants";

import {
  fetchFirstOk,
  saveWithDuplicateCheck,
} from "./api";

import { useAdminProfile } from "./hooks/useAdminProfile";
import { useStep1Catalogs } from "./hooks/useStep1Catalogs";
import { useStep2Catalogs } from "./hooks/useStep2Catalogs";

import Modal from "./components/Modal";
import EditStepGeneral from "./components/EditStepGeneral";
import EditStepItems from "./components/EditStepItems";

import {
  buildSyntheticOption,
  coerceId,
  dedupeById,
  descCacheKey,
  displayStatus,
  existsInCatalog,
  fmtMoney,
  getAdministrativeUnitRefFromRequisition,
  getDescTextFromCache,
  getLabelDefault,
  getLockedProductIdFromItems,
  getObjectDisplay,
  getProductLabel,
  getUnitLabel,
  isNoAplicaLabel,
  isRealId,
  labelFrom,
  normalizeDescriptionRow,
  normalizeId,
  normalizeMoneyInput,
  numOrNull,
  sanitizeMoneyInput,
} from "./utils";

const emptyItemForm = {
  _cid: null,
  id: null,
  product: "",
  product_display: "",
  quantity: "",
  unit: "",
  unit_display: "",
  description: "",
  description_text: "",
  description_display: "",
  manual_description: "",
  estimated_unit_cost: "",
  estimated_total: "",
};

const READONLY_HEADER_FIELDS = new Set([
  "requesting_department",
  "project",
  "funding_source",
  "budget_unit",
  "agreement",
  "tender",
  "external_service",
  "status",
]);

const EXPLICIT_AGREEMENT_FUNDING_CODES = new Set(["16000", "16001"]);
const NO_APLICA_AGREEMENT_CODE = "0000";
const NO_APLICA_AGREEMENT_DESCRIPTION = "NO APLICA";

const getCatalogCode = (row) => String(row?.code || "").trim();
const normalizeComparableText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w\s./-]/g, "")
    .toUpperCase()
    .trim();
const isNoAplicaAgreement = (agreement) =>
  getCatalogCode(agreement) === NO_APLICA_AGREEMENT_CODE &&
  normalizeComparableText(agreement?.description) ===
    normalizeComparableText(NO_APLICA_AGREEMENT_DESCRIPTION);

const normalizeHistoryRow = (row) => {
  const quantity =
    row?.quantity === null || typeof row?.quantity === "undefined"
      ? ""
      : String(row.quantity);

  const estimatedUnitCost =
    row?.estimated_unit_cost === null || typeof row?.estimated_unit_cost === "undefined"
      ? ""
      : String(row.estimated_unit_cost);

  const estimatedTotal =
    row?.estimated_total === null || typeof row?.estimated_total === "undefined"
      ? ""
      : String(row.estimated_total);

  return {
    manual_description: String(row?.manual_description || "").trim(),
    quantity,
    unit: row?.unit ? String(row.unit) : "",
    unit_display: String(row?.unit_display || "").trim(),
    estimated_unit_cost: estimatedUnitCost,
    estimated_total: estimatedTotal,
    source_requisition_id: row?.source_requisition_id ?? null,
    source_requisition_folio: row?.source_requisition_folio ?? null,
    source_created_at: row?.source_created_at || null,
  };
};

export default function RequisitionEditWizard({ requisition, onSaved }) {
  const navigate = useNavigate();

  const { loadingMe } = useAdminProfile();
  const { catalogs, loadingStep1 } = useStep1Catalogs();

  const [step, setStep] = useState(1);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const createdItemIdsThisSessionRef = useRef(new Set());

  const [statusHistory, setStatusHistory] = useState([]);

  const [headerForm, setHeaderForm] = useState({
    administrative_unit: "",
    administrative_unit_ref: "",
    requesting_department: "",
    project: "",
    funding_source: "",
    budget_unit: "",
    agreement: "",
    tender: "",
    external_service: "",
    created_at: "",
    requisition_reason: "",
    status: "registered",
  });

  const [ackCostRealistic, setAckCostRealistic] = useState(false);

  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyItemForm);

  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [catalogModalProduct, setCatalogModalProduct] = useState("");

  const [registerForm, setRegisterForm] = useState({
    product: "",
    unit: "",
    text: "",
    estimated_unit_cost: "",
  });

  const [busyRegister, setBusyRegister] = useState(false);
  const [busySave, setBusySave] = useState(false);
  const [busyItemOp, setBusyItemOp] = useState(false);

  const [quoteDraftInvalid, setQuoteDraftInvalid] = useState(false);
  const [realAmountNeedsUpdate, setRealAmountNeedsUpdate] = useState(false);
  const [realAmountCaptured, setRealAmountCaptured] = useState(true);

  const [realTotalsById, setRealTotalsById] = useState({});
  const [realUnitById, setRealUnitById] = useState({});
  const [realAmountReason, setRealAmountReason] = useState("");
  const [busyRealAmount, setBusyRealAmount] = useState(false);
  const [manualHistoryOptions, setManualHistoryOptions] = useState([]);
  const [loadingManualHistory, setLoadingManualHistory] = useState(false);

  const [realAmountBase, setRealAmountBase] = useState(0);
  const [realAmountLogs, setRealAmountLogs] = useState([]);
  const manualHistoryRequestSeq = useRef(0);
  const loadedManualHistoryProductIdRef = useRef("");

  const canEditRequisition = Boolean(requisition?.can_edit_requisition);
  const canEditHeader = Boolean(requisition?.can_edit_requisition_header);
  const canEditRequestingDepartment = Boolean(requisition?.can_edit_requesting_department);
  const canChangeStatus = Boolean(requisition?.can_change_requisition_status);
  const canManageRealAmounts = Boolean(requisition?.can_manage_real_amounts);

  const currentStatusLabel = useMemo(
    () => displayStatus(headerForm.status || requisition?.status || "registered"),
    [headerForm.status, requisition?.status]
  );
  const isDraftEdit = useMemo(
    () => String(requisition?.status || headerForm.status || "").trim().toLowerCase() === "draft",
    [headerForm.status, requisition?.status]
  );

  const editBlockedMessage = useMemo(() => {
    if (!requisition) return "";
    if (canEditRequisition) return "";

    const rawStatus = String(requisition?.status || headerForm.status || "").trim().toLowerCase();
    if (rawStatus && rawStatus !== "registered") {
      return `Solo puedes modificar requisiciones con estatus Registrado. Estatus actual: ${currentStatusLabel}.`;
    }

    return "No tienes permisos para modificar esta requisición.";
  }, [canEditRequisition, currentStatusLabel, headerForm.status, requisition]);

  const step1CatalogsLoaded = useMemo(
    () => Object.keys(CATALOG_META).every((k) => Array.isArray(catalogs[k])),
    [catalogs]
  );

  const selectedFundingSource = useMemo(
    () =>
      (catalogs?.funding_source || []).find(
        (row) => String(row.id) === String(headerForm.funding_source || "")
      ) || null,
    [catalogs, headerForm.funding_source]
  );

  const shouldEnableAgreementField = useMemo(
    () => EXPLICIT_AGREEMENT_FUNDING_CODES.has(getCatalogCode(selectedFundingSource)),
    [selectedFundingSource]
  );

  const noAplicaAgreementOption = useMemo(
    () =>
      (catalogs?.agreement || []).find(
        (row) => isNoAplicaAgreement(row)
      ) || null,
    [catalogs]
  );

  const visibleAgreementCatalog = useMemo(() => {
    const base = catalogs?.agreement || [];
    if (!shouldEnableAgreementField) return base;
    return base.filter((row) => !isNoAplicaAgreement(row));
  }, [catalogs, shouldEnableAgreementField]);

  const displayCatalogs = useMemo(
    () => ({
      ...catalogs,
      agreement: visibleAgreementCatalog,
    }),
    [catalogs, visibleAgreementCatalog]
  );

  const displayCatalogMeta = CATALOG_META;

  const disabledHeaderFields = useMemo(() => {
    const disabled = [];

    if (!canEditRequestingDepartment) {
      disabled.push("requesting_department");
    }

    if (!shouldEnableAgreementField) {
      disabled.push("agreement");
    }

    return disabled;
  }, [canEditRequestingDepartment, shouldEnableAgreementField]);

  const originalTenderId = coerceId(requisition?.tender);
  const currentTenderId = String(headerForm.tender || "");

  const tenderLabelFromCatalog = useMemo(
    () => labelFrom(catalogs?.tender || [], headerForm.tender, getLabelDefault) || "",
    [catalogs, headerForm.tender]
  );

  const tenderLabelFromReq = useMemo(() => {
    const isStillOriginalTender =
      headerForm.tender &&
      String(headerForm.tender) === String(originalTenderId) &&
      !tenderLabelFromCatalog;

    if (!isStillOriginalTender) return "";

    if (typeof requisition?.tender === "object" && requisition?.tender) {
      return getLabelDefault(requisition.tender);
    }

    return requisition?.tender_label || "";
  }, [headerForm.tender, originalTenderId, tenderLabelFromCatalog, requisition]);

  const currentTenderLabel = tenderLabelFromCatalog || tenderLabelFromReq || "";

  const manualItemsMode = useMemo(() => {
    return String(currentTenderLabel).toUpperCase().includes("NO APLICA");
  }, [currentTenderLabel]);

  const selectedTender = useMemo(
    () => (catalogs?.tender || []).find((t) => String(t.id) === String(headerForm.tender ?? "")),
    [catalogs, headerForm.tender]
  );

  const selectedTenderText = useMemo(
    () =>
      selectedTender?.name ||
      selectedTender?.label ||
      selectedTender?.description ||
      (selectedTender ? getLabelDefault(selectedTender) : "") ||
      requisition?.tender_display ||
      requisition?.tender_name ||
      currentTenderLabel ||
      "",
    [selectedTender, requisition, currentTenderLabel]
  );

  const showQuotesAndAck = useMemo(() => isNoAplicaLabel(selectedTenderText), [selectedTenderText]);
  const showRealAmountSection = useMemo(
    () => canManageRealAmounts && showQuotesAndAck,
    [canManageRealAmounts, showQuotesAndAck]
  );
  const disableRealByQuoteDraft = showQuotesAndAck ? quoteDraftInvalid : false;

  const disableSave =
    !canEditRequisition ||
    (showRealAmountSection && realAmountNeedsUpdate && !realAmountCaptured);

  const disableSaveByAck =
    !canEditRequisition ||
    (showQuotesAndAck && !ackCostRealistic);

  const disableRealAmountByAck =
    !canEditRequisition ||
    (showQuotesAndAck && !ackCostRealistic);

  const lockedProductId = useMemo(() => getLockedProductIdFromItems(items || []), [items]);
  const isProductLocked = lockedProductId !== null;

  const effectiveFormProductId = useMemo(() => {
    if (isProductLocked && lockedProductId !== null) return String(lockedProductId);
    return String(form.product || "");
  }, [isProductLocked, lockedProductId, form.product]);

  const {
    products,
    units,
    descOptions,
    setDescOptions,
    descCache,
    setDescCache,
    loadingCatalogs,
    catalogModalDescs,
    setCatalogModalDescs,
  } = useStep2Catalogs({
    step,
    currentTenderId,
    manualItemsMode,
    items,
    effectiveFormProductId,
    showCatalogModal,
    catalogModalProduct,
  });

  const lockedProductLabel = useMemo(() => {
    if (!lockedProductId) return "";

    for (const item of items || []) {
      const itemProductId = normalizeId(item?.product);
      if (itemProductId === lockedProductId) {
        return (
          item?.product_display ||
          labelFrom(products, lockedProductId, getProductLabel) ||
          ""
        );
      }
    }

    return labelFrom(products, lockedProductId, getProductLabel) || "";
  }, [items, lockedProductId, products]);

  const effectiveFormProductDisplay = useMemo(() => {
    if (isProductLocked) {
      return (
        lockedProductLabel ||
        labelFrom(products, lockedProductId, getProductLabel) ||
        ""
      );
    }

    return (
      form.product_display ||
      labelFrom(products, form.product, getProductLabel) ||
      ""
    );
  }, [isProductLocked, lockedProductLabel, products, lockedProductId, form.product_display, form.product]);

  const filteredProducts = useMemo(() => {
    let base = products || [];

    if (
      effectiveFormProductId &&
      !base.some((p) => String(p.id) === String(effectiveFormProductId))
    ) {
      const fallbackLabel = effectiveFormProductDisplay || `ID: ${effectiveFormProductId}`;
      base = [
        buildSyntheticOption(
          effectiveFormProductId,
          `${fallbackLabel} (actual no disponible)`
        ),
        ...base,
      ];
    }

    return base;
  }, [products, effectiveFormProductId, effectiveFormProductDisplay]);

  const displayUnits = useMemo(() => {
    let base = units || [];
    if (form.unit && !base.some((u) => String(u.id) === String(form.unit))) {
      const fallbackLabel = form.unit_display || `ID: ${form.unit}`;
      base = [buildSyntheticOption(form.unit, `${fallbackLabel} (actual no disponible)`), ...base];
    }
    return base;
  }, [units, form.unit, form.unit_display]);

  const displayDescOptions = useMemo(() => {
    let base = descOptions || [];

    if (form.description && !base.some((d) => String(d.id) === String(form.description))) {
      const syntheticText =
        form.description_text ||
        form.description_display ||
        `ID: ${form.description}`;

      base = [
        {
          id: form.description,
          productId: effectiveFormProductId || "",
          tenderId: currentTenderId,
          unitId: form.unit || "",
          unitLabel: form.unit_display || "",
          text: `${syntheticText} (actual no disponible)`,
          estimated_unit_cost: form.estimated_unit_cost || "",
        },
        ...base,
      ];
    }

    return base;
  }, [
    descOptions,
    form.description,
    form.description_text,
    form.description_display,
    form.estimated_unit_cost,
    form.unit,
    form.unit_display,
    currentTenderId,
    effectiveFormProductId,
  ]);

  const visibleCatalogModalDescs = useMemo(() => catalogModalDescs || [], [catalogModalDescs]);

  const currentUnitLabel = useMemo(() => {
    if (!manualItemsMode) return "";
    if (!form.unit) return "";
    return labelFrom(units, form.unit, getUnitLabel) || form.unit_display || "";
  }, [manualItemsMode, form.unit, form.unit_display, units]);

  const markRealAmountPending = () => {
    if (!showRealAmountSection) return;
    setRealAmountNeedsUpdate(true);
    setRealAmountCaptured(false);
    setRealAmountReason("");
  };

  useEffect(() => {
    if (!showQuotesAndAck) {
      if (quoteDraftInvalid) setQuoteDraftInvalid(false);
      if (ackCostRealistic) {
        setAckCostRealistic(false);
        setHasUnsavedChanges(true);
      }
    }
  }, [showQuotesAndAck, quoteDraftInvalid, ackCostRealistic]);

  const toNumSafe = useCallback((v) => {
    if (v === null || v === undefined || v === "") return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }, []);

  const round2 = useCallback((n) => {
    return Math.round((toNumSafe(n) + Number.EPSILON) * 100) / 100;
  }, [toNumSafe]);

  const qtyOf = useCallback((it) => toNumSafe(it?.quantity), [toNumSafe]);

  const estimatedTotalOf = useCallback((it) => toNumSafe(it?.estimated_total), [toNumSafe]);

  const estimatedUnitOf = useCallback((it) => {
    const q = qtyOf(it);
    if (!q) return 0;
    return round2(estimatedTotalOf(it) / q);
  }, [qtyOf, estimatedTotalOf, round2]);

  const realUnitOf = useCallback((it) => {
    const raw = realUnitById?.[it.id];
    if (raw === null || raw === undefined || raw === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) ? round2(n) : null;
  }, [realUnitById, round2]);

  const realRowTotalOf = useCallback((it) => {
    const q = qtyOf(it);
    const u = realUnitOf(it);
    if (!q || u === null) return null;
    return round2(q * u);
  }, [qtyOf, realUnitOf, round2]);

  const realTotalSum = useMemo(() => {
    return (items || []).reduce((acc, it) => {
      const rowTotal = realRowTotalOf(it);
      return acc + (rowTotal ?? 0);
    }, 0);
  }, [items, realRowTotalOf]);

  const handleRealUnitChange = (itemId, rawValue) => {
    const cleaned = sanitizeMoneyInput(rawValue);

    setRealUnitById((prev) => ({
      ...(prev || {}),
      [itemId]: cleaned,
    }));

    const row = (items || []).find((it) => Number(it.id) === Number(itemId));
    const q = qtyOf(row);
    const n = Number(cleaned);
    const computed =
      cleaned === "" || !Number.isFinite(n) || q <= 0 ? "" : round2(q * n).toFixed(2);

    setRealTotalsById((prev) => ({
      ...(prev || {}),
      [itemId]: computed,
    }));
  };

  const handleRealUnitBlur = (itemId) => {
    const normalized = normalizeMoneyInput(realUnitById?.[itemId] ?? "");

    setRealUnitById((prev) => ({
      ...(prev || {}),
      [itemId]: normalized,
    }));

    const row = (items || []).find((it) => Number(it.id) === Number(itemId));
    const q = qtyOf(row);
    const n = Number(normalized);
    const computed =
      normalized === "" || !Number.isFinite(n) || q <= 0 ? "" : round2(q * n).toFixed(2);

    setRealTotalsById((prev) => ({
      ...(prev || {}),
      [itemId]: computed,
    }));
  };

  const autoFillTotalIfPossible = useCallback((next) => {
    const qty = Number(next.quantity);
    const uc = Number(next.estimated_unit_cost);
    if (Number.isFinite(qty) && qty > 0 && Number.isFinite(uc) && uc > 0) {
      const total = (qty * uc).toFixed(2);
      return { ...next, estimated_total: total };
    }
    return { ...next, estimated_total: "" };
  }, []);

  const setFormPatched = useCallback((patch) => {
    setForm((prev) => autoFillTotalIfPossible({ ...prev, ...patch }));
  }, [autoFillTotalIfPossible]);

  const loadManualHistoryForProduct = useCallback(
    async (productId, { force = false } = {}) => {
      const normalizedProductId = productId ? String(productId) : "";

      if (!(step === 2 && manualItemsMode && normalizedProductId)) {
        loadedManualHistoryProductIdRef.current = "";
        setManualHistoryOptions([]);
        setLoadingManualHistory(false);
        return;
      }

      if (!force && loadedManualHistoryProductIdRef.current === normalizedProductId) {
        return;
      }

      const requestId = ++manualHistoryRequestSeq.current;
      setLoadingManualHistory(true);

      try {
        const response = await apiClient.get(
          `/catalogs/products/${encodeURIComponent(normalizedProductId)}/user-history/`
        );
        const rows = Array.isArray(response?.data) ? response.data : [];

        if (requestId !== manualHistoryRequestSeq.current) return;

        loadedManualHistoryProductIdRef.current = normalizedProductId;
        setManualHistoryOptions(
          rows
            .map(normalizeHistoryRow)
            .filter((row) => row.manual_description)
        );
      } catch (error) {
        if (requestId !== manualHistoryRequestSeq.current) return;
        loadedManualHistoryProductIdRef.current = "";
        console.error("Error loading manual item history:", error);
        setManualHistoryOptions([]);
      } finally {
        if (requestId === manualHistoryRequestSeq.current) {
          setLoadingManualHistory(false);
        }
      }
    },
    [manualItemsMode, step]
  );

  const ensureManualHistoryLoaded = useCallback(
    (productId = effectiveFormProductId) => {
      if (!(step === 2 && manualItemsMode && productId)) return;
      void loadManualHistoryForProduct(productId);
    },
    [effectiveFormProductId, loadManualHistoryForProduct, manualItemsMode, step]
  );

  useEffect(() => {
    if (step === 2 && manualItemsMode) return;
    loadedManualHistoryProductIdRef.current = "";
    setManualHistoryOptions([]);
    setLoadingManualHistory(false);
  }, [manualItemsMode, step]);

  const goNext = () => setStep((s) => Math.min(2, s + 1));
  const goPrev = () => setStep((s) => Math.max(1, s - 1));

  const onChangeHeader = (key, value) => {
    if (!canEditRequisition) return;
    if (key === "status") return;
    if (READONLY_HEADER_FIELDS.has(key) && !canEditHeader) return;
    if (key === "requesting_department" && !canEditRequestingDepartment) return;
    if (key === "agreement" && !shouldEnableAgreementField) return;

    setHeaderForm((prev) => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
  };

  useEffect(() => {
    if (!step1CatalogsLoaded) return;

    setHeaderForm((prev) => {
      const currentAgreementId = String(prev.agreement || "");
      const noAplicaAgreementId = String(noAplicaAgreementOption?.id || "");

      if (!shouldEnableAgreementField) {
        if (!noAplicaAgreementOption) return prev;
        if (currentAgreementId === noAplicaAgreementId) return prev;
        return { ...prev, agreement: String(noAplicaAgreementOption.id) };
      }

      if (!prev.agreement) return prev;

      const isCurrentAgreementNoAplica =
        noAplicaAgreementId && currentAgreementId === noAplicaAgreementId;
      const isCurrentAgreementVisible = visibleAgreementCatalog.some(
        (row) => String(row.id) === currentAgreementId
      );

      if (!isCurrentAgreementNoAplica && isCurrentAgreementVisible) {
        return prev;
      }

      return { ...prev, agreement: "" };
    });
  }, [
    noAplicaAgreementOption,
    shouldEnableAgreementField,
    step1CatalogsLoaded,
    visibleAgreementCatalog,
  ]);

  const buildHeaderExtraOption = (key) => {
    const currentValue = headerForm[key];
    if (!currentValue) return null;
    if (!step1CatalogsLoaded) return null;
    const sourceCatalog = key === "agreement" ? visibleAgreementCatalog : catalogs[key];
    if (existsInCatalog(sourceCatalog, currentValue)) return null;

    const raw = requisition?.[key];
    const baseLabel = raw && typeof raw === "object" ? getLabelDefault(raw) : `ID: ${currentValue}`;

    return buildSyntheticOption(currentValue, `${baseLabel} (actual no disponible)`);
  };

  const invalidHeaderRefs = useMemo(() => {
    if (!step1CatalogsLoaded) return [];

    const invalid = [];
    if (!existsInCatalog(catalogs.requesting_department, headerForm.requesting_department)) invalid.push("Departamento Solicitante");
    if (!existsInCatalog(catalogs.project, headerForm.project)) invalid.push("Proyecto");
    if (!existsInCatalog(catalogs.funding_source, headerForm.funding_source)) invalid.push("Fuente de Financiamiento");
    if (!existsInCatalog(catalogs.budget_unit, headerForm.budget_unit)) invalid.push("Unidad Presupuestal");
    if (!existsInCatalog(visibleAgreementCatalog, headerForm.agreement)) invalid.push("Convenio");
    if (!existsInCatalog(catalogs.tender, headerForm.tender)) invalid.push("Licitación");
    if (!existsInCatalog(catalogs.external_service, headerForm.external_service)) invalid.push("Servicio Externo / Académico");
    return invalid;
  }, [catalogs, headerForm, step1CatalogsLoaded, visibleAgreementCatalog]);

  const blockingInvalidHeaderRefs = useMemo(() => {
    if (!canEditHeader) return [];
    return invalidHeaderRefs;
  }, [canEditHeader, invalidHeaderRefs]);

  const resolveHeaderIdForSave = useCallback(
    (key) => {
      const sourceValue = canEditHeader
        ? headerForm[key]
        : coerceId(requisition?.[key]) || headerForm[key];

      if (key === "agreement" && !shouldEnableAgreementField) {
        return numOrNull(noAplicaAgreementOption?.id ?? sourceValue);
      }

      return numOrNull(sourceValue);
    },
    [canEditHeader, headerForm, noAplicaAgreementOption, requisition, shouldEnableAgreementField]
  );

  useEffect(() => {
    if (!requisition) return;

    createdItemIdsThisSessionRef.current = new Set();

    const administrativeUnitRef = getAdministrativeUnitRefFromRequisition(requisition);

    const createdISO = requisition.created_at ? new Date(requisition.created_at) : null;
    const yyyyMMdd = createdISO
      ? `${createdISO.getFullYear()}-${String(createdISO.getMonth() + 1).padStart(2, "0")}-${String(
          createdISO.getDate()
        ).padStart(2, "0")}`
      : "";

    setHeaderForm((prev) => ({
      ...prev,
      administrative_unit:
        typeof requisition.administrative_unit === "object" && requisition.administrative_unit
          ? getLabelDefault(requisition.administrative_unit)
          : requisition.administrative_unit || "",
      administrative_unit_ref: administrativeUnitRef ?? "",
      requesting_department: coerceId(requisition.requesting_department),
      project: coerceId(requisition.project),
      funding_source: coerceId(requisition.funding_source),
      budget_unit: coerceId(requisition.budget_unit),
      agreement: coerceId(requisition.agreement),
      tender: coerceId(requisition.tender),
      external_service: coerceId(requisition.external_service),
      created_at: yyyyMMdd,
      requisition_reason: requisition.requisition_reason || "",
      status: requisition.status || "registered",
    }));

    setStatusHistory(Array.isArray(requisition.status_history) ? requisition.status_history : []);

    setAckCostRealistic(Boolean(requisition.ack_cost_realistic));
    setRealAmountLogs(Array.isArray(requisition.real_amount_logs) ? requisition.real_amount_logs : []);

    const nextItems = (requisition.items || []).map((it) => ({
      _cid: String(it.id),
      id: it.id,
      product: coerceId(it.product),
      product_display:
        it.product_display ||
        getObjectDisplay(it.product, getProductLabel) ||
        (coerceId(it.product) ? `ID: ${coerceId(it.product)}` : ""),
      quantity: Number(it.quantity ?? 0),
      unit: coerceId(it.unit),
      unit_display:
        it.unit_display ||
        getObjectDisplay(it.unit, getUnitLabel) ||
        (coerceId(it.unit) ? `ID: ${coerceId(it.unit)}` : ""),
      description: coerceId(it.description),
      manual_description: it.manual_description || "",
      description_text: it.description_text || "",
      description_display: it.description_display || "",
      estimated_unit_cost:
        it.estimated_unit_cost === null || typeof it.estimated_unit_cost === "undefined"
          ? ""
          : Number(it.estimated_unit_cost),
      estimated_total:
        it.estimated_total === null || typeof it.estimated_total === "undefined"
          ? ""
          : Number(it.estimated_total),
      real_total:
        it.real_total === null || typeof it.real_total === "undefined"
          ? ""
          : Number(it.real_total),
      real_unit_cost:
        it.real_unit_cost === null || typeof it.real_unit_cost === "undefined"
          ? ""
          : Number(it.real_unit_cost),
    }));

    setItems(nextItems);

    const nextRealMap = {};
    const nextRealUnitMap = {};

    nextItems.forEach((it) => {
      const suggestedTotal = it.real_total !== "" ? it.real_total : it.estimated_total;

      nextRealMap[it.id] =
        suggestedTotal === null || typeof suggestedTotal === "undefined" || suggestedTotal === ""
          ? ""
          : String(Number(suggestedTotal).toFixed(2));

      if (it.real_unit_cost !== "" && Number.isFinite(Number(it.real_unit_cost))) {
        nextRealUnitMap[it.id] = String(Number(it.real_unit_cost).toFixed(2));
      } else if (it.real_total !== "" && Number.isFinite(Number(it.real_total)) && Number(it.quantity) > 0) {
        nextRealUnitMap[it.id] = String((Number(it.real_total) / Number(it.quantity)).toFixed(2));
      } else if (it.estimated_unit_cost !== "" && Number.isFinite(Number(it.estimated_unit_cost))) {
        nextRealUnitMap[it.id] = String(Number(it.estimated_unit_cost).toFixed(2));
      } else {
        nextRealUnitMap[it.id] = "";
      }
    });

    setRealTotalsById(nextRealMap);
    setRealUnitById(nextRealUnitMap);

    const baseNum = Number(requisition.real_amount ?? 0);
    setRealAmountBase(Number.isFinite(baseNum) ? baseNum : 0);

    const hasItems = Array.isArray(nextItems) && nextItems.length > 0;
    const allHaveReal = hasItems ? nextItems.every((it) => it.real_total !== "" && Number(it.real_total) > 0) : true;

    if (showRealAmountSection && hasItems && !allHaveReal) {
      setRealAmountNeedsUpdate(true);
      setRealAmountCaptured(false);
      setRealAmountReason("");
    } else {
      setRealAmountNeedsUpdate(false);
      setRealAmountCaptured(true);
      setRealAmountReason("");
    }

    setHasUnsavedChanges(false);
    setForm(emptyItemForm);
  }, [requisition, showRealAmountSection]);

  useEffect(() => {
    if (step !== 2) return;
    setForm(emptyItemForm);
    setDescOptions([]);
  }, [step, currentTenderId, manualItemsMode, setDescOptions]);

  useEffect(() => {
    if (step !== 2) return;
    if (!isProductLocked) return;

    const nextProductId = String(lockedProductId);
    const nextProductLabel =
      lockedProductLabel ||
      labelFrom(products, nextProductId, getProductLabel) ||
      "";

    setForm((prev) => {
      const prevProductId = normalizeId(prev.product);

      if (prevProductId === lockedProductId) {
        return {
          ...prev,
          product: nextProductId,
          product_display: prev.product_display || nextProductLabel,
        };
      }

      return autoFillTotalIfPossible({
        ...prev,
        product: nextProductId,
        product_display: nextProductLabel,
        description: "",
        description_text: "",
        description_display: "",
        manual_description: "",
        estimated_unit_cost: "",
        estimated_total: "",
        quantity: "",
        unit: "",
        unit_display: "",
      });
    });

    if (manualItemsMode && nextProductId) {
      void loadManualHistoryForProduct(nextProductId, { force: true });
    }
  }, [
    step,
    isProductLocked,
    lockedProductId,
    lockedProductLabel,
    products,
    autoFillTotalIfPossible,
    manualItemsMode,
    loadManualHistoryForProduct,
  ]);

  const handleDescriptionChange = (descId) => {
    if (manualItemsMode) return;

    if (!descId) {
      setFormPatched({
        description: "",
        description_text: "",
        description_display: "",
        unit: "",
        unit_display: "",
        estimated_unit_cost: "",
        estimated_total: "",
      });
      return;
    }

    const selected = (displayDescOptions || []).find((d) => String(d.id) === String(descId));
    const cost = selected?.estimated_unit_cost;
    const nextCost = cost !== null && typeof cost !== "undefined" && cost !== "" ? String(cost) : "";
    const nextUnitId = selected?.unitId ? String(selected.unitId) : "";
    const nextUnitLabel =
      selected?.unitLabel ||
      labelFrom(displayUnits, nextUnitId, getUnitLabel) ||
      "";

    setFormPatched({
      description: descId,
      description_text: selected?.text || "",
      description_display: selected?.text ? `${selected.text} (ID: ${descId})` : "",
      unit: nextUnitId,
      unit_display: nextUnitLabel,
      estimated_unit_cost: nextCost,
      manual_description: "",
    });
  };

  const handleManualHistorySelection = useCallback(
    (entry) => {
      if (!entry || !manualItemsMode) return;

      const nextUnitId = entry.unit ? String(entry.unit) : "";
      const nextUnitLabel =
        entry.unit_display ||
        labelFrom(displayUnits, nextUnitId, getUnitLabel) ||
        "";
      const nextUnitCost =
        entry.estimated_unit_cost !== null &&
        typeof entry.estimated_unit_cost !== "undefined" &&
        entry.estimated_unit_cost !== ""
          ? String(entry.estimated_unit_cost)
          : "";

      setFormPatched({
        manual_description: entry.manual_description || "",
        quantity: entry.quantity ? String(entry.quantity) : "",
        unit: nextUnitId,
        unit_display: nextUnitLabel,
        estimated_unit_cost: nextUnitCost,
        description: "",
        description_text: "",
        description_display: "",
      });
    },
    [displayUnits, manualItemsMode, setFormPatched]
  );

  useEffect(() => {
    if (manualItemsMode) return;

    const did = form.description;
    if (!did) return;

    const selected = (displayDescOptions || []).find((d) => String(d.id) === String(did));
    if (!selected) return;

    const nextCost =
      selected.estimated_unit_cost !== null &&
      typeof selected.estimated_unit_cost !== "undefined" &&
      selected.estimated_unit_cost !== ""
        ? String(selected.estimated_unit_cost)
        : "";

    const nextUnitId = selected.unitId ? String(selected.unitId) : "";
    const nextUnitLabel =
      selected.unitLabel ||
      labelFrom(displayUnits, nextUnitId, getUnitLabel) ||
      "";

    setForm((prev) => {
      const next = autoFillTotalIfPossible({
        ...prev,
        description_text: selected?.text || prev.description_text,
        description_display: selected?.text ? `${selected.text} (ID: ${did})` : prev.description_display,
        unit: nextUnitId || prev.unit,
        unit_display: nextUnitLabel || prev.unit_display,
        estimated_unit_cost: nextCost || prev.estimated_unit_cost,
      });

      const changed =
        String(next.description_text || "") !== String(prev.description_text || "") ||
        String(next.description_display || "") !== String(prev.description_display || "") ||
        String(next.unit || "") !== String(prev.unit || "") ||
        String(next.unit_display || "") !== String(prev.unit_display || "") ||
        String(next.estimated_unit_cost || "") !== String(prev.estimated_unit_cost || "") ||
        String(next.estimated_total || "") !== String(prev.estimated_total || "");

      return changed ? next : prev;
    });
  }, [form.description, displayDescOptions, manualItemsMode, displayUnits, autoFillTotalIfPossible]);

  const handleCancelToList = async () => {
    if (busyItemOp || busySave || busyRealAmount) {
      alert("Hay una operación en curso. Espera a que termine antes de cancelar.");
      return;
    }

    if (hasUnsavedChanges) {
      if (!window.confirm("Hay cambios sin guardar. ¿Salir de todos modos?")) return;
    } else {
      if (!window.confirm("¿Salir sin guardar cambios?")) return;
    }

    const createdIds = Array.from(createdItemIdsThisSessionRef.current || []);
    if (createdIds.length > 0) {
      const wantRollback = window.confirm(
        `Detecté ${createdIds.length} partida(s) creada(s) en esta edición.\n\n¿Quieres eliminarlas antes de salir?`
      );

      if (wantRollback) {
        setBusyItemOp(true);
        try {
          const results = await Promise.allSettled(
            createdIds.map((id) =>
              apiClient.delete(`/requisitions/${requisition.id}/items/${id}/`)
            )
          );

          const failed = results
            .map((r, i) => ({ r, id: createdIds[i] }))
            .filter((x) => x.r.status === "rejected")
            .map((x) => {
              const err = x.r.reason;
              const data = err?.response?.data;
              const msg = data?.detail || (data ? JSON.stringify(data) : err?.message || "Error");
              return `ID ${x.id}: ${msg}`;
            });

          if (failed.length > 0) {
            alert(
              "Se intentó hacer rollback, pero algunas partidas no se pudieron eliminar:\n\n" +
                failed.join("\n")
            );
          }
        } catch (err) {
          console.error(err);
          alert("No se pudo hacer rollback de las partidas creadas. Revisa la consola.");
        } finally {
          setBusyItemOp(false);
        }
      }
    }

    navigate(LIST_ROUTE);
  };

  const addOrUpdateItem = async (e) => {
    e.preventDefault();

    if (!canEditRequisition) {
      alert(editBlockedMessage || "No tienes permisos para modificar esta requisición.");
      return;
    }

    const { id, quantity, unit, description, manual_description, estimated_unit_cost } = form;

    const productToUse = effectiveFormProductId || String(form.product || "");
    const productDisplayToUse =
      effectiveFormProductDisplay ||
      labelFrom(products, productToUse, getProductLabel) ||
      `ID: ${productToUse}`;

    if (!productToUse || !quantity) {
      alert("Completa Objeto del Gasto y Cantidad.");
      return;
    }

    if (!manualItemsMode && !currentTenderId) {
      alert("Selecciona una licitación válida antes de registrar partidas.");
      return;
    }

    if (!manualItemsMode && !description) {
      alert("Completa Descripción (catálogo).");
      return;
    }

    if (manualItemsMode && !unit) {
      alert("Completa Unidad.");
      return;
    }

    if (manualItemsMode && !String(manual_description || "").trim()) {
      alert("Completa Descripción (manual).");
      return;
    }

    const qty = Number(quantity);
    if (!Number.isFinite(qty) || qty <= 0) {
      alert("Cantidad debe ser mayor a 0.");
      return;
    }

    if (!existsInCatalog(products, productToUse)) {
      alert("El Objeto del Gasto seleccionado ya no está activo o no está disponible. Selecciona uno activo.");
      return;
    }

    let resolvedUnitId = String(unit || "");
    let resolvedUnitDisplay =
      labelFrom(displayUnits, resolvedUnitId, getUnitLabel) ||
      form.unit_display ||
      (resolvedUnitId ? `ID: ${resolvedUnitId}` : "");

    let resolvedUnitCostNum = Number(estimated_unit_cost);
    let selectedDesc = null;

    if (manualItemsMode) {
      if (!existsInCatalog(units, unit)) {
        alert("La Unidad seleccionada ya no está activa o no está disponible. Selecciona una activa.");
        return;
      }

      if (!Number.isFinite(resolvedUnitCostNum) || resolvedUnitCostNum <= 0) {
        alert("El Costo unitario es obligatorio y debe ser mayor a 0.");
        return;
      }
    } else {
      selectedDesc = (descOptions || []).find((d) => String(d.id) === String(description));

      if (!selectedDesc) {
        alert("La descripción seleccionada ya no está activa o no corresponde a la licitación y objeto del gasto seleccionados.");
        return;
      }

      resolvedUnitId = String(selectedDesc.unitId || "");
      resolvedUnitDisplay =
        selectedDesc.unitLabel ||
        labelFrom(displayUnits, resolvedUnitId, getUnitLabel) ||
        resolvedUnitDisplay;

      if (!resolvedUnitId) {
        alert("No se pudo resolver la Unidad de Medida desde la descripción seleccionada.");
        return;
      }

      resolvedUnitCostNum = Number(selectedDesc.estimated_unit_cost);
      if (!Number.isFinite(resolvedUnitCostNum) || resolvedUnitCostNum <= 0) {
        alert("El Costo unitario (desde catálogo) es obligatorio y debe ser mayor a 0.");
        return;
      }
    }

    const totalNum = Number((qty * resolvedUnitCostNum).toFixed(2));

    const payload = {
      product: Number(productToUse),
      quantity: qty,
      unit: Number(resolvedUnitId),
      estimated_unit_cost: Number(resolvedUnitCostNum.toFixed(2)),
      estimated_total: totalNum,
      ...(manualItemsMode
        ? { description: null, manual_description: String(manual_description || "").trim() }
        : { description: Number(description), manual_description: null }),
    };

    const productDisplayLocal = productDisplayToUse;
    const unitDisplayLocal = resolvedUnitDisplay || `ID: ${resolvedUnitId}`;

    let descTextLocal = "";
    let descDisplayLocal = "";

    if (manualItemsMode) {
      descTextLocal = String(manual_description || "").trim();
      descDisplayLocal = descTextLocal;
    } else {
      descTextLocal =
        selectedDesc?.text ||
        getDescTextFromCache(descCache, currentTenderId, Number(productToUse), Number(description)) ||
        "";
      descDisplayLocal = descTextLocal
        ? `${descTextLocal} (ID: ${Number(description)})`
        : `ID: ${Number(description)}`;
    }

    setBusyItemOp(true);
    try {
      if (isRealId(id)) {
        const res = await apiClient.patch(
          `/requisitions/${requisition.id}/items/${Number(id)}/`,
          payload
        );
        const updated = res.data;

        markRealAmountPending();

        setItems((prev) =>
          prev.map((row) =>
            Number(row.id) === Number(updated.id)
              ? {
                  ...row,
                  _cid: String(updated.id),
                  id: updated.id,
                  ...payload,
                  product_display:
                    updated.product_display ||
                    getObjectDisplay(updated.product, getProductLabel) ||
                    productDisplayLocal,
                  unit_display:
                    updated.unit_display ||
                    getObjectDisplay(updated.unit, getUnitLabel) ||
                    unitDisplayLocal,
                  manual_description: updated.manual_description ?? payload.manual_description ?? "",
                  description_text: updated.description_text || descTextLocal,
                  description_display: updated.description_display || descDisplayLocal,
                  real_total:
                    row.real_total === "" || typeof row.real_total === "undefined"
                      ? ""
                      : Number(row.real_total),
                  real_unit_cost:
                    updated.real_unit_cost === null || typeof updated.real_unit_cost === "undefined"
                      ? row.real_unit_cost === "" || typeof row.real_unit_cost === "undefined"
                        ? ""
                        : Number(row.real_unit_cost)
                      : Number(updated.real_unit_cost),
                }
              : row
          )
        );

        setRealTotalsById((prev) => {
          const key = Number(updated.id);
          const cur = prev?.[key];
          if (cur == null || String(cur).trim() === "") {
            return { ...(prev || {}), [key]: totalNum.toFixed(2) };
          }
          return prev;
        });

        setRealUnitById((prev) => {
          const key = Number(updated.id);
          const cur = prev?.[key];
          if (cur == null || String(cur).trim() === "") {
            return { ...(prev || {}), [key]: Number(resolvedUnitCostNum).toFixed(2) };
          }
          return prev;
        });
      } else {
        const res = await apiClient.post(`/requisitions/${requisition.id}/items/`, payload);
        const created = res.data;

        if (isRealId(created?.id)) {
          createdItemIdsThisSessionRef.current.add(Number(created.id));
        }

        markRealAmountPending();

        setItems((prev) => [
          ...prev,
          {
            _cid: String(created.id),
            id: created.id,
            ...payload,
            product_display:
              created.product_display ||
              getObjectDisplay(created.product, getProductLabel) ||
              productDisplayLocal,
            unit_display:
              created.unit_display ||
              getObjectDisplay(created.unit, getUnitLabel) ||
              unitDisplayLocal,
            manual_description: created.manual_description ?? payload.manual_description ?? "",
            description_text: created.description_text || descTextLocal,
            description_display: created.description_display || descDisplayLocal,
            real_total:
              created.real_total === null || typeof created.real_total === "undefined"
                ? ""
                : Number(created.real_total),
            real_unit_cost:
              created.real_unit_cost === null || typeof created.real_unit_cost === "undefined"
                ? ""
                : Number(created.real_unit_cost),
          },
        ]);

        if (isRealId(created?.id)) {
          setRealTotalsById((prev) => ({
            ...(prev || {}),
            [Number(created.id)]: totalNum.toFixed(2),
          }));
          setRealUnitById((prev) => ({
            ...(prev || {}),
            [Number(created.id)]: Number(resolvedUnitCostNum).toFixed(2),
          }));
        }
      }

      setHasUnsavedChanges(true);
      setForm(emptyItemForm);
    } catch (err) {
      console.error(err);
      const data = err?.response?.data;
      alert(`No se pudo guardar la partida.\n\n${data ? JSON.stringify(data, null, 2) : err?.message || ""}`);
    } finally {
      setBusyItemOp(false);
    }
  };

  const editRow = (row) => {
    if (!canEditRequisition) {
      alert(editBlockedMessage || "No tienes permisos para modificar esta requisición.");
      return;
    }

    setForm((prev) =>
      autoFillTotalIfPossible({
        ...prev,
        _cid: row._cid,
        id: row.id ?? null,
        product: String(row.product || ""),
        product_display: String(row.product_display || ""),
        quantity: String(row.quantity || ""),
        unit: String(row.unit || ""),
        unit_display: String(row.unit_display || ""),
        description: String(row.description || ""),
        description_text: String(row.description_text || ""),
        description_display: String(row.description_display || ""),
        manual_description: String(row.manual_description || ""),
        estimated_unit_cost:
          row.estimated_unit_cost === "" ? "" : String(row.estimated_unit_cost ?? ""),
        estimated_total:
          row.estimated_total === "" ? "" : String(row.estimated_total ?? ""),
      })
    );

    if (step !== 2) setStep(2);
  };

  const deleteRow = async (_cid) => {
    if (!canEditRequisition) {
      alert(editBlockedMessage || "No tienes permisos para modificar esta requisición.");
      return;
    }

    if (!window.confirm("¿Eliminar este artículo?")) return;

    const row = (items || []).find((r) => String(r._cid) === String(_cid));
    const realId = row?.id;

    setBusyItemOp(true);
    try {
      if (isRealId(realId)) {
        await apiClient.delete(`/requisitions/${requisition.id}/items/${Number(realId)}/`);
        createdItemIdsThisSessionRef.current.delete(Number(realId));
      }

      markRealAmountPending();

      setItems((prev) => prev.filter((r) => r._cid !== _cid));
      setHasUnsavedChanges(true);

      if (isRealId(realId)) {
        setRealTotalsById((prev) => {
          const next = { ...(prev || {}) };
          delete next[Number(realId)];
          return next;
        });

        setRealUnitById((prev) => {
          const next = { ...(prev || {}) };
          delete next[Number(realId)];
          return next;
        });
      }

      if (String(form._cid) === String(_cid)) {
        setForm(emptyItemForm);
      }
    } catch (err) {
      console.error(err);
      const data = err?.response?.data;
      alert(`No se pudo eliminar.\n\n${data ? JSON.stringify(data, null, 2) : err?.message || ""}`);
    } finally {
      setBusyItemOp(false);
    }
  };

  const openClassifier = () => {
    window.open("/docs/clasificador.pdf", "_blank", "noopener");
  };

  const openCatalogModal = () => {
    if (manualItemsMode) return;
    setCatalogModalProduct(effectiveFormProductId || "");
    setCatalogModalDescs([]);
    setShowCatalogModal(true);
  };

  const openRegisterModal = () => {
    if (manualItemsMode) return;
    if (!canEditRequisition) {
      alert(editBlockedMessage || "No tienes permisos para modificar esta requisición.");
      return;
    }

    setRegisterForm({
      product: effectiveFormProductId || "",
      unit: "",
      text: "",
      estimated_unit_cost: "",
    });
    setShowRegisterModal(true);
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    if (manualItemsMode) return;

    if (!canEditRequisition) {
      alert(editBlockedMessage || "No tienes permisos para modificar esta requisición.");
      return;
    }

    if (!currentTenderId) {
      alert("Selecciona una licitación válida antes de registrar una descripción.");
      return;
    }

    if (!registerForm.product || !registerForm.unit || !registerForm.text.trim()) {
      alert("Selecciona Objeto del Gasto, Unidad y escribe la descripción.");
      return;
    }

    const costNum = Number(registerForm.estimated_unit_cost);
    if (!Number.isFinite(costNum) || costNum <= 0) {
      alert("Captura un costo válido (> 0).");
      return;
    }

    if (!existsInCatalog(products, registerForm.product)) {
      alert("El Objeto del Gasto seleccionado ya no está activo o no está disponible.");
      return;
    }

    if (!existsInCatalog(units, registerForm.unit)) {
      alert("La Unidad seleccionada ya no está activa o no está disponible.");
      return;
    }

    setBusyRegister(true);
    try {
      const payload = {
        tender: Number(currentTenderId),
        product: Number(registerForm.product),
        unit: Number(registerForm.unit),
        text: registerForm.text.trim(),
        estimated_unit_cost: Number(costNum.toFixed(2)),
      };

      const resp = await apiClient.post(STEP2_SPECS.itemDescriptionsPostUrl, payload);
      const reloadProductId = String(registerForm.product);

      try {
        const list = await fetchFirstOk(
          STEP2_SPECS.itemDescriptionsUrls(currentTenderId, reloadProductId)
        );

        const normalized = dedupeById(
          list
            .map(normalizeDescriptionRow)
            .filter((d) => String(d.productId) === String(reloadProductId))
            .filter((d) => String(d.tenderId) === String(currentTenderId))
        ).sort((a, b) => String(a.text || "").localeCompare(String(b.text || ""), "es"));

        setDescCache((prev) => ({
          ...prev,
          [descCacheKey(currentTenderId, reloadProductId)]: normalized,
        }));

        if (String(effectiveFormProductId) === reloadProductId) {
          setDescOptions(normalized);
        }

        if (String(catalogModalProduct) === reloadProductId) {
          setCatalogModalDescs(normalized);
        }
      } catch {
        // silencio
      }

      const createdId = String(resp.data?.id ?? "");
      const createdText = String(resp.data?.text ?? payload.text ?? "");
      const createdUnitId = String(resp.data?.unit ?? registerForm.unit ?? "");
      const createdUnitLabel =
        resp.data?.unit_display ||
        resp.data?.unit_name ||
        labelFrom(displayUnits, createdUnitId, getUnitLabel) ||
        "";
      const createdCost = String(resp.data?.estimated_unit_cost ?? payload.estimated_unit_cost ?? "");

      setFormPatched({
        product: String(registerForm.product),
        product_display: labelFrom(products, registerForm.product, getProductLabel),
        description: createdId,
        description_text: createdText,
        description_display: createdText ? `${createdText} (ID: ${createdId})` : "",
        unit: createdUnitId,
        unit_display: createdUnitLabel,
        manual_description: "",
        estimated_unit_cost: createdCost,
      });

      setShowRegisterModal(false);
      alert("Descripción registrada correctamente.");
    } catch (err) {
      console.error(err);
      const data = err?.response?.data;
      alert(`No se pudo registrar la descripción.\n\n${data ? JSON.stringify(data, null, 2) : ""}`);
    } finally {
      setBusyRegister(false);
    }
  };

  const doSaveAll = async ({ silent = false, bypassRealAmountBlock = false } = {}) => {
    if (!canEditRequisition) {
      if (!silent) {
        alert(editBlockedMessage || "No tienes permisos para modificar esta requisición.");
      }
      throw new Error("Blocked: edit not allowed");
    }

    if (showQuotesAndAck && !ackCostRealistic) {
      if (!silent) {
        alert('Debes confirmar "costo aproximado pero realista" para poder guardar.');
      }
      throw new Error("Blocked: ack cost realistic required");
    }

    if (!bypassRealAmountBlock && disableSave) {
      if (!silent) {
        alert(
          "No puedes guardar cambios todavía.\n\nDebes capturar el monto real POR PARTIDA (con auditoría) antes de guardar la requisición."
        );
      }
      throw new Error("Blocked: real totals required");
    }

    if (blockingInvalidHeaderRefs.length > 0) {
      if (!silent) {
        alert(
          "No se puede guardar porque hay catálogos inactivos o no disponibles seleccionados:\n\n- " +
            blockingInvalidHeaderRefs.join("\n- ") +
            "\n\nSelecciona valores activos antes de guardar."
        );
      }
      throw new Error("Blocked: inactive header refs");
    }

    const administrativeUnitRef =
      numOrNull(headerForm.administrative_unit_ref) ??
      getAdministrativeUnitRefFromRequisition(requisition);

    if (!administrativeUnitRef) {
      if (!silent) {
        alert(
          "No se puede guardar la requisición porque falta la Unidad Administrativa asociada (administrative_unit_ref).\n\nRecarga la requisición o verifica que el backend la esté enviando correctamente."
        );
      }
      throw new Error("Blocked: administrative unit missing");
    }

    const payload = {
      administrative_unit_ref: administrativeUnitRef,
      requesting_department: resolveHeaderIdForSave("requesting_department"),
      project: resolveHeaderIdForSave("project"),
      funding_source: resolveHeaderIdForSave("funding_source"),
      budget_unit: resolveHeaderIdForSave("budget_unit"),
      agreement: resolveHeaderIdForSave("agreement"),
      tender: resolveHeaderIdForSave("tender"),
      external_service: resolveHeaderIdForSave("external_service"),
      requisition_reason: headerForm.requisition_reason ?? "",
      ack_cost_realistic: ackCostRealistic,
    };

    if (isDraftEdit) {
      payload.status = "registered";
    }

    const result = await saveWithDuplicateCheck({
      reqId: requisition.id,
      payload,
      method: "put",
    });

    if (!result.ok && result.cancelled) {
      if (!silent) alert("Envío/guardado cancelado: revisa datos para evitar duplicado.");
      throw new Error("Blocked: duplicate cancelled");
    }

    const updated = result.data;

    try {
      const updatedAdministrativeUnitRef =
        getAdministrativeUnitRefFromRequisition(updated) ?? administrativeUnitRef;

      setHeaderForm((prev) => ({
        ...prev,
        administrative_unit:
          typeof updated.administrative_unit === "object" && updated.administrative_unit
            ? getLabelDefault(updated.administrative_unit)
            : updated.administrative_unit || prev.administrative_unit || "",
        administrative_unit_ref: updatedAdministrativeUnitRef,
        requesting_department: coerceId(updated.requesting_department),
        project: coerceId(updated.project),
        funding_source: coerceId(updated.funding_source),
        budget_unit: coerceId(updated.budget_unit),
        agreement: coerceId(updated.agreement),
        tender: coerceId(updated.tender),
        external_service: coerceId(updated.external_service),
        requisition_reason: updated.requisition_reason || "",
        status: updated.status || prev.status,
      }));

      setStatusHistory(Array.isArray(updated.status_history) ? updated.status_history : []);
      setAckCostRealistic(Boolean(updated.ack_cost_realistic));

      if (Array.isArray(updated.items)) {
        const next = (updated.items || []).map((it) => ({
          _cid: String(it.id),
          id: it.id,
          product: coerceId(it.product),
          product_display:
            it.product_display ||
            getObjectDisplay(it.product, getProductLabel) ||
            (coerceId(it.product) ? `ID: ${coerceId(it.product)}` : ""),
          quantity: Number(it.quantity ?? 0),
          unit: coerceId(it.unit),
          unit_display:
            it.unit_display ||
            getObjectDisplay(it.unit, getUnitLabel) ||
            (coerceId(it.unit) ? `ID: ${coerceId(it.unit)}` : ""),
          description: coerceId(it.description),
          manual_description: it.manual_description || "",
          description_text: it.description_text || "",
          description_display: it.description_display || "",
          estimated_unit_cost:
            it.estimated_unit_cost === null || typeof it.estimated_unit_cost === "undefined"
              ? ""
              : Number(it.estimated_unit_cost),
          estimated_total:
            it.estimated_total === null || typeof it.estimated_total === "undefined"
              ? ""
              : Number(it.estimated_total),
          real_total:
            it.real_total === null || typeof it.real_total === "undefined"
              ? ""
              : Number(it.real_total),
          real_unit_cost:
            it.real_unit_cost === null || typeof it.real_unit_cost === "undefined"
              ? ""
              : Number(it.real_unit_cost),
        }));

        setItems(next);

        const nextMap = {};
        const nextUnitMap = {};

        next.forEach((x) => {
          const suggested = x.real_total !== "" ? x.real_total : x.estimated_total;
          nextMap[x.id] =
            suggested === null || typeof suggested === "undefined" || suggested === ""
              ? ""
              : String(Number(suggested).toFixed(2));

          if (x.real_unit_cost !== "" && Number.isFinite(Number(x.real_unit_cost))) {
            nextUnitMap[x.id] = String(Number(x.real_unit_cost).toFixed(2));
          } else if (x.real_total !== "" && Number.isFinite(Number(x.real_total)) && Number(x.quantity) > 0) {
            nextUnitMap[x.id] = String((Number(x.real_total) / Number(x.quantity)).toFixed(2));
          } else if (x.estimated_unit_cost !== "" && Number.isFinite(Number(x.estimated_unit_cost))) {
            nextUnitMap[x.id] = String(Number(x.estimated_unit_cost).toFixed(2));
          } else {
            nextUnitMap[x.id] = "";
          }
        });

        setRealTotalsById(nextMap);
        setRealUnitById(nextUnitMap);
      }

      setRealAmountLogs(Array.isArray(updated.real_amount_logs) ? updated.real_amount_logs : []);
      const rnum = Number(updated.real_amount ?? 0);
      setRealAmountBase(Number.isFinite(rnum) ? rnum : 0);
    } catch {
      // no rompas guardado
    }

    setHasUnsavedChanges(false);

    if (!silent) {
      const extra = result.forced ? "\n\n(Confirmaste un posible duplicado.)" : "";
      alert(`Requisición #${requisition.id} guardada correctamente.${extra}`);
    }

    onSaved?.(updated);

    return updated;
  };

  const saveAll = async () => {
    if (!canEditRequisition) {
      alert(editBlockedMessage || "No tienes permisos para modificar esta requisición.");
      return;
    }

    setBusySave(true);
    try {
      await doSaveAll({ silent: false });
    } catch (err) {
      const msg = String(err?.message || "");
      if (msg.includes("Blocked:")) return;

      console.error(err);
      const data = err?.response?.data;
      if (data) alert(`No se pudo guardar.\n\n${JSON.stringify(data, null, 2)}`);
      else alert("No se pudo guardar. Revisa los datos.");
    } finally {
      setBusySave(false);
    }
  };

  const saveRealTotals = async () => {
    try {
      if (!showQuotesAndAck) return;

      if (!canEditRequisition) {
        alert(editBlockedMessage || "No tienes permisos para modificar esta requisición.");
        return;
      }

      if (!canManageRealAmounts) {
        alert("Solo Superusuario o superior pueden capturar el monto real.");
        return;
      }

      if (quoteDraftInvalid) {
        window.alert(
          "Tienes una cotización seleccionada pero sin partidas marcadas.\n\n" +
            "Completa los checkboxes o quita el PDF para poder capturar el monto real."
        );
        throw new Error("Blocked: quote draft incomplete");
      }

      if (showQuotesAndAck && !ackCostRealistic) {
        window.alert(
          'Para capturar el monto real, primero marca: "Confirmo que el costo aproximado pero realista ha sido verificado (requisito para imprimir/exportar PDF)."'
        );
        throw new Error("Blocked: ack cost realistic required");
      }

      if (!Array.isArray(items) || items.length === 0) {
        alert("No puedes capturar el monto real si la requisición no tiene partidas registradas.");
        throw new Error("Blocked: real amount requires items");
      }

      const reason = realAmountReason.trim();
      if (!reason) {
        alert("Debes escribir el motivo (auditoría).");
        return;
      }

      const missing = [];
      const invalid = [];
      const invalidQty = [];

      const payloadItems = items
        .filter((it) => isRealId(it.id))
        .map((it) => {
          const rawUnit = String(realUnitById?.[it.id] ?? "").trim();

          if (!rawUnit) {
            missing.push(it.id);
            return { id: it.id, real_unit_amount: null, real_total: null };
          }

          const unitNum = Number(rawUnit);
          if (!Number.isFinite(unitNum) || unitNum <= 0) {
            invalid.push(it.id);
            return { id: it.id, real_unit_amount: rawUnit, real_total: null };
          }

          const qty = Number(it?.quantity ?? 0);
          if (!Number.isFinite(qty) || qty <= 0) {
            invalidQty.push(it.id);
            return { id: it.id, real_unit_amount: unitNum.toFixed(2), real_total: null };
          }

          const rowTotal = round2(qty * unitNum);

          return {
            id: it.id,
            real_unit_amount: unitNum.toFixed(2),
            real_total: rowTotal.toFixed(2),
          };
        });

      if (missing.length > 0) {
        alert(
          `Faltan montos reales unitarios en las partidas: ${missing.join(", ")}.\n\nCaptura un monto unitario (>0) en cada partida.`
        );
        return;
      }

      if (invalid.length > 0) {
        alert(
          `Hay montos reales unitarios inválidos (<=0 o no numéricos) en partidas: ${invalid.join(", ")}.\n\nCorrige los valores.`
        );
        return;
      }

      if (invalidQty.length > 0) {
        alert(
          `Hay partidas con cantidad inválida (<=0) y no se puede calcular el monto real: ${invalidQty.join(", ")}.\n\nCorrige la cantidad antes de guardar.`
        );
        return;
      }

      setBusyRealAmount(true);
      try {
        if (showQuotesAndAck) {
          const ackResult = await saveWithDuplicateCheck({
            reqId: requisition.id,
            payload: { ack_cost_realistic: true },
            method: "patch",
          });

          if (!ackResult.ok && ackResult.cancelled) {
            alert("Guardado cancelado (posible duplicado). No se guardaron montos reales.");
            return;
          }

          if (ackResult?.data) {
            setAckCostRealistic(Boolean(ackResult.data.ack_cost_realistic));
          }
        }

        await apiClient.post(`/requisitions/${requisition.id}/set-real-totals/`, {
          reason,
          items: payloadItems,
        });

        try {
          const refreshed = await apiClient.get(`/requisitions/${requisition.id}/`);
          const r = refreshed.data;

          const rnum = Number(r.real_amount ?? 0);
          setRealAmountBase(Number.isFinite(rnum) ? rnum : 0);

          setRealAmountLogs(Array.isArray(r.real_amount_logs) ? r.real_amount_logs : []);
          setAckCostRealistic(Boolean(r.ack_cost_realistic));
          setStatusHistory(Array.isArray(r.status_history) ? r.status_history : []);

          if (Array.isArray(r.items)) {
            const nextItems = (r.items || []).map((it) => ({
              _cid: String(it.id),
              id: it.id,
              product: coerceId(it.product),
              product_display:
                it.product_display ||
                getObjectDisplay(it.product, getProductLabel) ||
                (coerceId(it.product) ? `ID: ${coerceId(it.product)}` : ""),
              quantity: Number(it.quantity ?? 0),
              unit: coerceId(it.unit),
              unit_display:
                it.unit_display ||
                getObjectDisplay(it.unit, getUnitLabel) ||
                (coerceId(it.unit) ? `ID: ${coerceId(it.unit)}` : ""),
              description: coerceId(it.description),
              manual_description: it.manual_description || "",
              description_text: it.description_text || "",
              description_display: it.description_display || "",
              estimated_unit_cost:
                it.estimated_unit_cost === null || typeof it.estimated_unit_cost === "undefined"
                  ? ""
                  : Number(it.estimated_unit_cost),
              estimated_total:
                it.estimated_total === null || typeof it.estimated_total === "undefined"
                  ? ""
                  : Number(it.estimated_total),
              real_total:
                it.real_total === null || typeof it.real_total === "undefined"
                  ? ""
                  : Number(it.real_total),
              real_unit_cost:
                it.real_unit_cost === null || typeof it.real_unit_cost === "undefined"
                  ? ""
                  : Number(it.real_unit_cost),
            }));

            setItems(nextItems);

            const nextMap = {};
            const nextUnitMap = {};

            nextItems.forEach((x) => {
              const val = x.real_total !== "" ? x.real_total : "";
              nextMap[x.id] = val === "" ? "" : String(Number(val).toFixed(2));

              if (x.real_unit_cost !== "" && Number.isFinite(Number(x.real_unit_cost))) {
                nextUnitMap[x.id] = String(Number(x.real_unit_cost).toFixed(2));
              } else if (x.real_total !== "" && Number.isFinite(Number(x.real_total)) && Number(x.quantity) > 0) {
                nextUnitMap[x.id] = String((Number(x.real_total) / Number(x.quantity)).toFixed(2));
              } else if (x.estimated_unit_cost !== "" && Number.isFinite(Number(x.estimated_unit_cost))) {
                nextUnitMap[x.id] = String(Number(x.estimated_unit_cost).toFixed(2));
              } else {
                nextUnitMap[x.id] = "";
              }
            });

            setRealTotalsById(nextMap);
            setRealUnitById(nextUnitMap);
          }

          onSaved?.(r);
        } catch {
          // silencio
        }

        setRealAmountNeedsUpdate(false);
        setRealAmountCaptured(true);
        setRealAmountReason("");

        if (hasUnsavedChanges) {
          try {
            setBusySave(true);
            await doSaveAll({ silent: true, bypassRealAmountBlock: true });
          } catch (err) {
            const msg = String(err?.message || "");
            if (!msg.includes("Blocked:")) console.error(err);
          } finally {
            setBusySave(false);
          }
        }

        alert(
          "Montos reales guardados con auditoría.\n\nSe capturó monto real unitario y se recalculó el monto real por partida automáticamente."
        );
      } catch (err) {
        const data = err?.response?.data;
        const msg = data || { message: err?.message || "Error desconocido" };
        alert(`No se pudo guardar montos reales.\n\n${JSON.stringify(msg, null, 2)}`);
      } finally {
        setBusyRealAmount(false);
      }
    } catch (err) {
      const msg = String(err?.message || "");
      if (msg.startsWith("Blocked:")) return;
      console.error(err);
      alert("No se pudo guardar montos reales. Revisa los datos.");
    }
  };

  if (!requisition) return <LoadingSpinner />;

  const baseEnabled =
    canEditRequisition &&
    (manualItemsMode ? !!effectiveFormProductId : !!form.description);

  return (
    <div className="max-w-5xl mx-auto">
      <div className="bg-blue-50 border border-blue-200 text-blue-900 px-4 py-3 rounded mb-4">
        <strong>Editar Requisición #{requisition.id}</strong>
        <div className="text-sm">Estatus actual: {currentStatusLabel}</div>
      </div>

      {!!editBlockedMessage && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-4">
          {editBlockedMessage}
        </div>
      )}

      <div className="flex items-center gap-2 mb-6">
        <div
          className={`px-3 py-1 rounded-full text-sm ${
            step === 1 ? "bg-blue-600 text-white" : "bg-gray-200"
          }`}
        >
          Paso 1 — Información General
        </div>
        <div
          className={`px-3 py-1 rounded-full text-sm ${
            step === 2 ? "bg-blue-600 text-white" : "bg-gray-200"
          }`}
        >
          Paso 2 — Registro de Partidas
        </div>
      </div>

      {step === 1 ? (
        <EditStepGeneral
          loadingStep1={loadingStep1}
          catalogs={displayCatalogs}
          catalogMeta={displayCatalogMeta}
          headerForm={headerForm}
          invalidHeaderRefs={invalidHeaderRefs}
          buildHeaderExtraOption={buildHeaderExtraOption}
          onChangeHeader={onChangeHeader}
          statusOptions={[]}
          adminLike={canManageRealAmounts}
          statusDirty={false}
          realAmountBase={realAmountBase}
          loadingMe={loadingMe}
          showRealAmountSection={showRealAmountSection}
          statusHistory={statusHistory}
          busyItemOp={busyItemOp}
          busySave={busySave}
          busyRealAmount={busyRealAmount}
          onCancel={handleCancelToList}
          onNext={goNext}
          canEditRequisition={canEditRequisition}
          canEditHeader={canEditHeader}
          canChangeStatus={canChangeStatus}
          canManageRealAmounts={canManageRealAmounts}
          currentStatusLabel={currentStatusLabel}
          editBlockedMessage={editBlockedMessage}
          disabledHeaderFields={disabledHeaderFields}
        />
      ) : (
        <EditStepItems
          requisitionId={requisition.id}
          manualItemsMode={manualItemsMode}
          currentUnitLabel={currentUnitLabel}
          loadingCatalogs={loadingCatalogs}
          showRealAmountSection={showRealAmountSection}
          realAmountNeedsUpdate={realAmountNeedsUpdate}
          realAmountCaptured={realAmountCaptured}
          form={form}
          setForm={setForm}
          setFormPatched={setFormPatched}
          baseEnabled={baseEnabled}
          effectiveFormProductId={effectiveFormProductId}
          currentTenderId={currentTenderId}
          isProductLocked={isProductLocked}
          filteredProducts={filteredProducts}
          displayDescOptions={displayDescOptions}
          displayUnits={displayUnits}
          manualHistoryOptions={manualHistoryOptions}
          loadingManualHistory={loadingManualHistory}
          ensureManualHistoryLoaded={ensureManualHistoryLoaded}
          busyItemOp={busyItemOp}
          handleDescriptionChange={handleDescriptionChange}
          handleManualHistorySelection={handleManualHistorySelection}
          addOrUpdateItem={addOrUpdateItem}
          openClassifier={openClassifier}
          openCatalogModal={openCatalogModal}
          openRegisterModal={openRegisterModal}
          showRegisterProductButton={SHOW_REGISTER_PRODUCT_BUTTON}
          items={items}
          products={products}
          units={units}
          descCache={descCache}
          editRow={editRow}
          deleteRow={deleteRow}
          showQuotesAndAck={showQuotesAndAck}
          onDraftInvalidChange={setQuoteDraftInvalid}
          ackCostRealistic={ackCostRealistic}
          onAckCostRealisticChange={(checked) => {
            if (!canEditRequisition) return;
            setAckCostRealistic(checked);
            setHasUnsavedChanges(true);
          }}
          realAmountBase={realAmountBase}
          realUnitById={realUnitById}
          realTotalsById={realTotalsById}
          handleRealUnitChange={handleRealUnitChange}
          handleRealUnitBlur={handleRealUnitBlur}
          qtyOf={qtyOf}
          estimatedUnitOf={estimatedUnitOf}
          estimatedTotalOf={estimatedTotalOf}
          realRowTotalOf={realRowTotalOf}
          realTotalSum={realTotalSum}
          realAmountReason={realAmountReason}
          onRealAmountReasonChange={setRealAmountReason}
          busyRealAmount={busyRealAmount}
          disableRealAmountByAck={disableRealAmountByAck}
          disableRealByQuoteDraft={disableRealByQuoteDraft || !canEditRequisition}
          onSaveRealTotals={saveRealTotals}
          realAmountLogs={realAmountLogs}
          onPrev={goPrev}
          onCancel={handleCancelToList}
          busySave={busySave}
          disableSave={disableSave}
          disableSaveByAck={disableSaveByAck}
          onSaveAll={saveAll}
          canEditRequisition={canEditRequisition}
          canEditHeader={canEditHeader}
          canManageRealAmounts={canManageRealAmounts}
          editBlockedMessage={editBlockedMessage}
          readOnly={!canEditRequisition}
        />
      )}

      {showCatalogModal && !manualItemsMode && (
        <Modal
          onClose={() => setShowCatalogModal(false)}
          title="Catálogo de Descripciones"
        >
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Objeto del Gasto
              </label>
              <select
                className="w-full border rounded px-2 py-1"
                value={catalogModalProduct}
                onChange={(e) => setCatalogModalProduct(e.target.value)}
              >
                <option value="">
                  {currentTenderId ? "— Selecciona —" : "Selecciona primero la Licitación"}
                </option>
                {filteredProducts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {getProductLabel(p)}
                  </option>
                ))}
              </select>
            </div>

            <div className="border rounded overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 text-left">Descripción</th>
                    <th className="px-2 py-2 text-left">Unidad</th>
                    <th className="px-2 py-2 text-right">Costo</th>
                  </tr>
                </thead>
                <tbody>
                  {catalogModalProduct && visibleCatalogModalDescs.length === 0 ? (
                    <tr>
                      <td className="px-2 py-3 text-gray-500" colSpan={3}>
                        Sin descripciones registradas.
                      </td>
                    </tr>
                  ) : (
                    visibleCatalogModalDescs.map((d) => (
                      <tr key={d.id} className="border-t">
                        <td className="px-2 py-2">{d.text}</td>
                        <td className="px-2 py-2">{d.unitLabel || "—"}</td>
                        <td className="px-2 py-2 text-right">
                          {fmtMoney(d.estimated_unit_cost)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300"
                onClick={() => setShowCatalogModal(false)}
              >
                Cerrar
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showRegisterModal && !manualItemsMode && (
        <Modal
          onClose={() => setShowRegisterModal(false)}
          title="Registrar Descripción"
        >
          <form onSubmit={submitRegister} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Objeto del Gasto
              </label>
              <select
                className="w-full border rounded px-2 py-1"
                value={registerForm.product}
                onChange={(e) =>
                  setRegisterForm((prev) => ({ ...prev, product: e.target.value }))
                }
              >
                <option value="">
                  {currentTenderId ? "— Selecciona —" : "Selecciona primero la Licitación"}
                </option>
                {filteredProducts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {getProductLabel(p)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Unidad</label>
              <select
                className="w-full border rounded px-2 py-1"
                value={registerForm.unit}
                onChange={(e) =>
                  setRegisterForm((prev) => ({ ...prev, unit: e.target.value }))
                }
              >
                <option value="">— Selecciona —</option>
                {displayUnits.map((u) => (
                  <option key={u.id} value={u.id}>
                    {getUnitLabel(u)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Descripción
              </label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={registerForm.text}
                onChange={(e) =>
                  setRegisterForm((prev) => ({ ...prev, text: e.target.value }))
                }
                placeholder="Escribe la nueva descripción"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Costo (obligatorio)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="w-full border rounded px-2 py-1"
                value={registerForm.estimated_unit_cost}
                onChange={(e) =>
                  setRegisterForm((prev) => ({
                    ...prev,
                    estimated_unit_cost: e.target.value,
                  }))
                }
                placeholder="0.00"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300"
                onClick={() => setShowRegisterModal(false)}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={busyRegister}
                className={`px-3 py-1 rounded text-white ${
                  busyRegister ? "bg-blue-400" : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {busyRegister ? "Registrando..." : "Registrar"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
